import { Link, useParams } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';
import { AsyncBoundary, Icon, useToast } from '@kollez/design-system';
import { apiGet } from '@/lib/api';
import { formatIndianDate, formatLPA } from '@kollez/shared';

interface Student {
  profile: {
    _id: string;
    user?: { _id: string; fullName?: string; email?: string; phone?: string; role?: string };
    academic?: {
      program?: { name?: string } | string;
      specialization?: { name?: string } | string;
      college?: { name?: string; slug?: string } | string;
      batchYear?: number;
      currentYear?: number;
      cgpa?: number;
      /** Number in demo seeds; `{current, history}` object in the real schema. */
      backlogs?: number | { current?: number; history?: unknown[] };
      rollNumber?: string;
    };
    placementStatus?: { status?: string; company?: string; ctc?: number };
    skills?: Array<{ name: string }>;
  };
  applications: Array<{
    _id: string;
    status?: string;
    drive?: { driveName?: string; driveDate?: string };
    createdAt?: string;
  }>;
  offers: Array<{ _id: string; status?: string; ctcInr?: number; releasedAt?: string }>;
}

function rel<T>(v: T | { name?: string } | null | undefined): string {
  if (!v) return '—';
  if (typeof v === 'string') return v;
  return (v as { name?: string }).name ?? '—';
}

function initials(name?: string): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
}

/** /group/students/:id — group-level view of one student. */
export function GroupStudentDetailPage() {
  const { id } = useParams();
  const toast = useToast();
  const query = useQuery({
    queryKey: ['group', 'student', id],
    queryFn: () => apiGet<Student>(`/group/students/${id}`),
    enabled: !!id,
  });

  const student = query.data;
  const u = student?.profile.user;
  const a = student?.profile.academic;

  return (
    <div className="stack">
      {/* F-080 pattern: page-header lives outside AsyncBoundary so the heading is present
          during loading/error and doesn't come + go with the fetch state. */}
      <div className="page-header">
        <div className="flex items-center gap-4 min-w-0">
          <span className="avatar avatar-xl">{initials(u?.fullName)}</span>
          <div className="ph-text">
            <p className="eyebrow">
              {rel(a?.program)} · {rel(a?.specialization)} · {a?.batchYear ? `Batch ${a.batchYear}` : ''}
              {rel(a?.college) !== '—' ? ` · ${rel(a?.college)}` : ''}
              {a?.rollNumber ? ` · roll ${a.rollNumber}` : ''}
            </p>
            <h1 className="h-page">{u?.fullName ?? 'Student'}</h1>
            <p className="subhead">Group view · same records the college's placement office sees.</p>
          </div>
        </div>
        <div className="ph-actions">
          <Link className="btn btn-outline" to="/profile-360"><Icon name="radar" size={14} />360° view</Link>
          {u?.phone && (
            <a
              className="btn btn-primary"
              href={`https://wa.me/${u.phone.replace(/\D/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => toast.show({ message: 'Opening WhatsApp with the student.' })}
            >
              <Icon name="message-circle" size={14} />WhatsApp
            </a>
          )}
        </div>
      </div>
      <AsyncBoundary kind="detail" loading={query.isLoading} error={query.error}>
        <div className="stack">
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
          <div className="card kpi">
            <span className="kpi-label">Status</span>
            <span className="kpi-value">{student?.profile.placementStatus?.status ?? 'Active'}</span>
            {student?.profile.placementStatus?.company && (
              <span className="kpi-meta">{student.profile.placementStatus.company}{student.profile.placementStatus.ctc ? ` · ${formatLPA(student.profile.placementStatus.ctc)}` : ''}</span>
            )}
          </div>
          <div className="card kpi">
            <span className="kpi-label">CGPA</span>
            <span className="kpi-value">{a?.cgpa != null ? String(a.cgpa) : '—'}</span>
            <span className="kpi-meta">{(() => {
              const n = typeof a?.backlogs === 'number' ? a.backlogs : (a?.backlogs?.current ?? 0);
              return n === 0 ? 'no backlogs' : `${n} backlog${n === 1 ? '' : 's'}`;
            })()}</span>
          </div>
          <div className="card kpi">
            <span className="kpi-label">Applications</span>
            <span className="kpi-value">{student?.applications.length ?? 0}</span>
            <span className="kpi-meta">this year</span>
          </div>
          <div className="card kpi">
            <span className="kpi-label">Offers</span>
            <span className="kpi-value">{student?.offers.length ?? 0}</span>
          </div>
        </div>

        <section className="card">
          <div className="card-head"><h2 className="h-section">Applications</h2></div>
          {(student?.applications.length ?? 0) === 0 ? (
            <div className="empty-state p-6 text-center">No applications yet</div>
          ) : (
            <div className="tbl-wrap">
              <table className="tbl">
                <thead><tr><th>Drive</th><th>Stage</th><th className="hide-mobile">Updated</th></tr></thead>
                <tbody>
                  {student?.applications.map((app) => (
                    <tr key={app._id}>
                      <td>{app.drive?.driveName ?? '—'}</td>
                      <td>
                        <span className={`badge ${(app.status ?? '').match(/accepted|hired|placed/i) ? 'badge-success' : ''}`}>
                          {app.status ?? '—'}
                        </span>
                      </td>
                      <td className="hide-mobile cell-sub">{app.createdAt ? formatIndianDate(new Date(app.createdAt)) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <p className="helper">Every action on this record is logged. Contact numbers are not shown to group admins by default; the WhatsApp button opens their app without revealing the number.</p>
        </div>
      </AsyncBoundary>
    </div>
  );
}
